import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonIcon,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Link,
  Skeleton,
  Text,
} from '@backstage/ui';
import {
  discoveryApiRef,
  fetchApiRef,
  iconsApiRef,
  useApi,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import { CardTitle } from '../ui';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { useOnValueChange } from './quietRefresh';
import {
  useCurrentAppDescriptor,
  useCurrentAppTitle,
} from '../shared/AppTypeContext';
import { ActionIcon } from './ActionIcon';
import {
  fetchCredentials,
  loginUrlOf,
  type CredentialsAccess,
  type CredentialsResponse,
} from './credentials';
import { projectEnvironmentsTranslationRef } from './translation';

/**
 * How long the "Copied" label stays on a button. Long enough to be read, short
 * enough that the button goes back to saying what it does.
 */
const COPIED_LABEL_MS = 2000;

/**
 * The width the two labels are held at, so that the login and the password line
 * up one under the other whatever they are translated to.
 */
const LABEL_WIDTH = '10rem';

export interface CredentialsCardProps {
  /**
   * The project of the page's route — its `waadoo.ovh/project` label. The
   * backend decides from it who may read the credentials.
   */
  project: string;
  /**
   * The environment's namespace, read off its ArgoCD Application
   * (`spec.destination.namespace`) by the page and never rebuilt here — the
   * same rule the Backups and Upload cards follow.
   */
  namespace: string;
  /**
   * The page's Refresh: a new value reads the card again, its state kept
   * (`useOnValueChange`) — a prop and not a new React key, which would drop
   * what the reader is in the middle of.
   */
  reloadSignal?: unknown;
  /**
   * The environment's address (`waadoo.ovh/url` of its Application), for the
   * link to its sign-in page; none without it.
   */
  url?: string;
  /**
   * What the page knows of the caller's right to the credentials
   * (`credentialsAccess`): `pending` — a Skeleton, nothing asked yet —,
   * `editors-only` or `admins-only` — the sentence saying who may read them,
   * and no request. Omitted: the card asks, and the route's 404 reads as
   * « cannot be read right now ».
   */
  access?: CredentialsAccess;
}

/**
 * The administrator credentials of one branch environment, so that signing in
 * to its application does not require `kubectl` — the core's « Accès » tab
 * for every type declaring the `credentials` feature (`useAccessTab`,
 * genericity plan, step 7, 2026-10-24; formerly Odoo's module's card).
 *
 * WHERE THE VALUES COME FROM. The type's chart keeps them in a Kubernetes
 * Secret of the environment's namespace — for Odoo, a password its PreSync job
 * generates on the environment's first init; nothing about it is in git, and
 * it differs per environment. The card reads it through the
 * `waadoo-credentials` backend route, which returns that one login/password
 * pair and NOT the namespace's other Secrets — see the header of
 * `plugins/waadoo-core-backend/src/plugins/credentials/plugin.ts` for why
 * that narrowing is the point of the route, and how the type's descriptor
 * (`spec.credentials`) names the Secret.
 *
 * WHO SEES THEM is decided by the `waadoo-credentials` backend, per project
 * — since 2026-10-29 the admin tier everywhere, the editors of the project
 * (`prj-<project>-editors`) on every branch but the production, nobody else —
 * not here: the card is offered to everyone who reaches the page. When the
 * page already knows the caller's level (`access`, from
 * `useProjectAccessAnswer`), the card says who may read them without asking,
 * as the Backups card does for its list; otherwise a refusal reads as
 * "cannot be read right now".
 *
 * NOTHING HAPPENS ON MOUNT beyond a pure GET, the rule the Backups card states:
 * opening a page must not create an object in the cluster.
 *
 * THE PASSWORD IS MASKED UNTIL ASKED FOR, and revealed one environment at a
 * time. Not a security boundary — the value is in the response either way — but
 * a page that opens with a password in plain sight is one that leaks it to
 * whoever walks past the screen.
 *
 * NO LINK TO THE INSTANCE ITSELF, deliberately: the « Ouvrir <application> »
 * button of the page's summary band, always visible above the tabs, already
 * opens it, and a second control leading to the same URL was dropped from the
 * environments table for costing more room than the shortcut it saved (see
 * `EnvironmentsCard`). A link to its SIGN-IN PAGE only when the type declares
 * one (`credentials.loginPath`, e.g. WordPress's `/wp-login.php`), the page
 * the credentials are typed into; Odoo declares none, so its card shows no
 * link, as before.
 */
export function CredentialsCard(props: CredentialsCardProps) {
  const { project, namespace, reloadSignal, url, access } = props;

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const iconsApi = useApi(iconsApiRef);
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const appTitle = useCurrentAppTitle();
  const loginUrl = loginUrlOf(
    url,
    useCurrentAppDescriptor()?.credentials.loginPath,
  );

  const refreshIcon = iconsApi.icon('refresh');

  const [data, setData] = useState<CredentialsResponse>();
  const [loading, setLoading] = useState(true);
  // Kept apart from `data` on purpose, and never used to clear it: a read that
  // fails after one that succeeded must leave the credentials on screen, with
  // one line saying they may be stale — the rule the Backups card states.
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  // Which field's copy is being confirmed, so the confirmation rides on the
  // button that was pressed rather than on both at once.
  const [copied, setCopied] = useState<'login' | 'password'>();
  const [copyFailed, setCopyFailed] = useState(false);
  // Bumped by Refresh and by the retry button; part of the load effect's key,
  // which is the whole mechanism.
  const [reloadKey, setReloadKey] = useState(0);
  useOnValueChange(reloadSignal, () => setReloadKey(key => key + 1));

  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    // Known in advance: nothing to ask (`access`). Asked again once it is
    // lifted — `pending` turning into an answer the route has to decide.
    if (access) {
      return undefined;
    }
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchCredentials({
          fetchApi,
          discoveryApi,
          project,
          namespace,
        });
        if (!active) {
          return;
        }
        setData(result);
        setError(null);
      } catch {
        if (!active) {
          return;
        }
        // Only that it failed, not why: the backend's message is an HTTP status
        // or a Kubernetes error, neither of which means anything on this screen.
        setError(t('credentials.unavailable'));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    // `load` turns every failure into state, so nothing can reject. The `active`
    // flag is what discards a late answer: the page cannot currently move from
    // one branch to another without unmounting this card, and this is what keeps
    // that true by accident rather than by contract.
    void load();

    return () => {
      active = false;
    };
  }, [project, namespace, reloadKey, fetchApi, discoveryApi, t, access]);

  useEffect(
    () => () => {
      clearTimeout(copyTimer.current);
    },
    [],
  );

  const copy = useCallback(
    async (field: 'login' | 'password', value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(field);
        setCopyFailed(false);
        // Cleared before arming, so clicking twice does not leave the first
        // timer to wipe the second confirmation early.
        clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(
          () => setCopied(undefined),
          COPIED_LABEL_MS,
        );
      } catch {
        // The clipboard is unavailable in a non-secure context and can be
        // refused outright. Said rather than swallowed: a button that does
        // nothing when pressed reads as broken.
        setCopied(undefined);
        setCopyFailed(true);
      }
    },
    [],
  );

  // Outlined (secondary) rather than tertiary, which read as plain text next
  // to the values.
  const renderCopyButton = (field: 'login' | 'password', value: string) => (
    <Button
      size="small"
      variant="secondary"
      iconStart={<ActionIcon name={copied === field ? 'check' : 'copy'} />}
      onPress={() => void copy(field, value)}
    >
      {copied === field ? t('credentials.copied') : t('credentials.copy')}
    </Button>
  );

  const renderBody = () => {
    if (access === 'pending' || (!access && loading)) {
      return <Skeleton />;
    }

    // A fact rather than a failure, in the register of `backups.notVisible`:
    // the route would only answer 404, so it is not asked.
    if (access === 'editors-only' || access === 'admins-only') {
      return (
        <Text variant="body-medium" color="secondary">
          {access === 'editors-only'
            ? t('credentials.editorsOnly')
            : t('credentials.adminsOnly')}
        </Text>
      );
    }

    // A failed read is a LINE, never the whole body — the rule the logs panel
    // states: what we last knew stays on screen, and the way forward with it.
    const errorLine = error ? (
      <Text variant="body-medium" color="danger">
        {error}
      </Text>
    ) : null;

    // Only reachable when the FIRST read of this namespace failed: the load
    // above keeps the previous body afterwards. Without the button this branch
    // would be a dead end, and nothing here retries on its own.
    if (!data) {
      return (
        <Flex direction="column" gap="3">
          <Flex align="center" gap="3">
            <Button
              size="small"
              variant="secondary"
              onPress={() => setReloadKey(key => key + 1)}
            >
              {t('credentials.retry')}
            </Button>
          </Flex>
          {errorLine}
        </Flex>
      );
    }

    // A fact rather than a failure, like the Backups card's "not configured":
    // an environment that has not run its first initialisation yet, or whose
    // type's Secret does not exist yet.
    if (!data.configured) {
      return (
        <Flex direction="column" gap="3">
          <Text variant="body-medium" color="primary">
            {t('credentials.notConfigured')}
          </Text>
          {errorLine}
        </Flex>
      );
    }

    const { login, password, secretName } = data;
    // The backend sets those three together whenever `configured` is true, so
    // this cannot happen — and it is handled rather than asserted away, because
    // a contract break must cost the card and not throw inside it.
    if (!login || !password || !secretName) {
      return (
        <Flex direction="column" gap="3">
          <Text variant="body-medium" color="danger">
            {t('credentials.unavailable')}
          </Text>
          {errorLine}
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap="3">
        <Flex align="center" gap="3" style={{ flexWrap: 'wrap' }}>
          <Text
            variant="body-small"
            color="secondary"
            style={{ minWidth: LABEL_WIDTH }}
          >
            {t('credentials.login')}
          </Text>
          <Text
            variant="body-medium"
            style={{ fontFamily: 'var(--bui-font-monospace)' }}
          >
            {login}
          </Text>
          {renderCopyButton('login', login)}
        </Flex>

        <Flex align="center" gap="3" style={{ flexWrap: 'wrap' }}>
          <Text
            variant="body-small"
            color="secondary"
            style={{ minWidth: LABEL_WIDTH }}
          >
            {t('credentials.password')}
          </Text>
          <Text
            variant="body-medium"
            style={{ fontFamily: 'var(--bui-font-monospace)' }}
          >
            {revealed ? password : '••••••••••••'}
          </Text>
          <Button
            size="small"
            variant="secondary"
            iconStart={
              <ActionIcon name={revealed ? 'visibility-off' : 'visibility'} />
            }
            onPress={() => setRevealed(current => !current)}
          >
            {revealed ? t('credentials.hide') : t('credentials.reveal')}
          </Button>
          {renderCopyButton('password', password)}
        </Flex>

        {/* The point of the card as much as the values are: the password is
            born with the environment and kept in a Kubernetes Secret, not in
            the repository. Naming that Secret says so without a sentence. */}
        <Text variant="body-small" color="secondary">
          {t('credentials.source', { secret: secretName })}
        </Text>

        {loginUrl && (
          <Text variant="body-small">
            <Link href={loginUrl} target="_blank" rel="noopener">
              {t('credentials.loginPage')}
            </Link>
          </Text>
        )}

        {copyFailed && (
          <Text variant="body-small" color="danger">
            {t('credentials.copyFailed')}
          </Text>
        )}

        {errorLine}
      </Flex>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle
          icon={waadooIcons.access}
          title={t('credentials.title', { app: appTitle })}
          description={t('credentials.subtitle')}
          actions={
            <ButtonIcon
              variant="tertiary"
              size="small"
              aria-label={t('card.refresh')}
              icon={refreshIcon ?? undefined}
              onClick={() => setReloadKey(key => key + 1)}
            />
          }
        />
      </CardHeader>
      <CardBody>{renderBody()}</CardBody>
    </Card>
  );
}
